import { cn } from '@invent/wl-ui-kit-next';
import { Badge, Button, Textarea } from '@invent/wl-ui-kit-next/ui';
import { Plus } from 'lucide-react';
import * as React from 'react';

import {
  AnswerRefField,
  optionsOf,
  type IPayloadContext
} from './payload-editors';
import {
  ChoiceField,
  FieldRow,
  NumberField,
  PayloadHeader,
  RawJsonField,
  RepeaterItem,
  StringListField,
  TextField,
  TriStateField,
  asArray,
  asObject,
  asString,
  emptyToNull,
  withMember,
  type JsonObject
} from './payload-fields';

/**
 * Form editors for a rule's `predicate` and its effects.
 *
 * ## Why this is a tree and the usage payloads were not
 *
 * A usage's `params` is a flat record: a handful of members, each a scalar or a short list.
 * A predicate is a **grammar** — `all` and `any` take arrays of predicates and `not` takes
 * one, so a condition nests to any depth. That is the whole difference, and it is why this is
 * a recursive component rather than a longer form.
 *
 * The server's shape is exact and unusual, so it is worth stating: **a node is an object with
 * exactly one key, and that key is the node's kind.** `{"all":[…]}`, `{"answer_eq":{…}}`.
 * An unknown key is a parse error rather than something that evaluates to false, which is what
 * makes a mistyped rule fail at publish instead of quietly never firing.
 *
 * ## What makes it usable by someone who does not write JSON
 *
 * Naming. `answer_cmp` is a wire token; "A numeric answer compared with a value" is the
 * question an analyst is actually asking. The kind picker is the only place the tokens appear,
 * and each one carries the sentence it means. Everything below the picker is selects over
 * vocabulary members — comparison operators, fact keys, database checks, path codes — so the
 * only free text left is a value, a message or a code.
 *
 * ## Depth is shown, not implied
 *
 * A nested condition is indented and carries a left rule in the parent's colour, because the
 * difference between "all of (A, any of (B, C))" and "all of (A, B, C)" is invisible in a flat
 * list and changes what the rule does.
 */

// ---------------------------------------------------------------------------
// The grammar, named
// ---------------------------------------------------------------------------

interface IKind {
  kind: string;
  label: string;
  /** What a fresh node of this kind looks like, so switching kind never leaves a half-shape. */
  blank: () => unknown;
}

/**
 * Every kind the server's converter accepts, in the order an author reaches for them:
 * grouping first, then the answer tests that make up most rules, then the outer edges.
 *
 * `PredicateKinds.All` on the server is the same fourteen, and `vocabulary.predicateKinds`
 * carries it — {@link usableKinds} intersects with that list, so a kind retired on the server
 * disappears here without a release, and one added there shows up as its raw token rather than
 * silently missing.
 */
const KINDS: IKind[] = [
  { kind: 'all', label: 'All of these are true', blank: () => [] },
  { kind: 'any', label: 'Any of these is true', blank: () => [] },
  { kind: 'not', label: 'This is not true', blank: () => ({ const: true }) },
  { kind: 'answer_eq', label: 'An answer is a particular option', blank: () => ({ ref: { usage: '' }, option: '' }) },
  { kind: 'answer_in', label: 'An answer is one of several options', blank: () => ({ ref: { usage: '' }, options: [] }) },
  { kind: 'answer_cmp', label: 'A numeric answer compared with a value', blank: () => ({ ref: { usage: '' }, op: '>=', value: 0 }) },
  { kind: 'selected_count', label: 'How many options are selected', blank: () => ({ ref: { usage: '' }, op: '>=', value: 1 }) },
  { kind: 'sum', label: 'A sum compared with a value or another answer', blank: () => ({ of: { usage: '' }, op: '==', against: { value: 100 } }) },
  { kind: 'repeat_count', label: 'How many rows a repeat group has', blank: () => ({ usage: '', op: '>=', value: 1 }) },
  { kind: 'fact_exists', label: 'An external fact exists', blank: () => ({ fact: '' }) },
  { kind: 'fact_cmp', label: 'An external fact compared with a value', blank: () => ({ fact: '', op: '>=', value: 0 }) },
  { kind: 'db_state', label: 'A database state check', blank: () => ({ check: '' }) },
  { kind: 'path_is', label: 'The respondent is on a particular path', blank: () => '' },
  { kind: 'const', label: 'Always true, or always false', blank: () => true }
];

const KIND_BY_CODE = new Map(KINDS.map((entry) => [entry.kind, entry]));

/**
 * The server's list, in this file's order, plus anything it knows that this file does not —
 * and always the kind already on the node.
 *
 * That last clause is not defensive padding. A kind retired on the server, or a vocabulary
 * that failed to load, would otherwise leave the picker showing a blank trigger over a rule
 * that does have a condition: the author could not read what they had, and the first touch of
 * the control would overwrite it. Offering the current kind keeps an unfamiliar rule legible
 * and editable.
 */
function usableKinds(context: IPayloadContext, current: Nullable<string>): IKind[] {
  const served = context.vocabulary?.predicateKinds ?? [];
  const offered = served.length === 0 ? KINDS.map((entry) => entry.kind) : served;

  const codes = new Set(offered);
  if (current !== null) codes.add(current);

  const known = KINDS.filter((entry) => codes.has(entry.kind));
  const unfamiliar = [...codes]
    .filter((kind) => !KIND_BY_CODE.has(kind))
    .map((kind) => ({ kind, label: kind, blank: () => ({}) }));

  return [...known, ...unfamiliar];
}

/** A node is `{kind: payload}` — one key, and the key is the kind. */
function readNode(value: unknown): Nullable<{ kind: string; payload: unknown }> {
  const object = asObject(value);
  if (!object) return null;
  const keys = Object.keys(object);
  if (keys.length !== 1) return null;
  return { kind: keys[0], payload: object[keys[0]] };
}

function makeNode(kind: string, payload: unknown): JsonObject {
  return { [kind]: payload };
}

// ---------------------------------------------------------------------------
// The editor
// ---------------------------------------------------------------------------

export function PredicateEditor({
  value,
  onChange,
  context,
  title = 'Condition',
  description = 'When this is true, the rule fires. A rule with no condition always fires.',
  rootLabel = 'This rule fires when',
  allowEmpty = true
}: {
  value: unknown;
  onChange: (next: unknown) => void;
  context: IPayloadContext;
  title?: string;
  description?: React.ReactNode;
  /** What the outermost node is introduced as. A nested predicate is not the rule's. */
  rootLabel?: string;
  /**
   * Off where the grammar makes the predicate mandatory — a `set_state` case's `when` is
   * `required` on the server, so offering to remove it would build an unpublishable effect.
   */
  allowEmpty?: boolean;
}) {
  const [json, setJson] = React.useState(false);
  const empty = value === null || value === undefined;

  // A mandatory predicate that arrived empty gets a node rather than a prompt: there is no
  // legal empty state to leave it in, so the form starts from something valid.
  React.useEffect(() => {
    if (!allowEmpty && empty && !context.disabled) onChange(makeNode('const', true));
  }, [allowEmpty, empty, context.disabled, onChange]);

  return (
    <div className="grid gap-3">
      <PayloadHeader label={title} description={description} json={json} onJson={setJson} />

      {json ? (
        <RawJsonField label="predicate" value={value} onChange={onChange} disabled={context.disabled} />
      ) : empty ? (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm text-muted-foreground">
            {allowEmpty ? 'No condition — this rule always applies.' : 'No condition set.'}
          </p>
          {!context.disabled && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onChange(makeNode('all', []))}
            >
              <Plus className="size-4" aria-hidden /> Add a condition
            </Button>
          )}
        </div>
      ) : (
        <>
          <PredicateNodeFields
            value={value}
            onChange={onChange}
            context={context}
            depth={0}
            rootLabel={rootLabel}
          />
          {allowEmpty && !context.disabled && (
            <Button variant="ghost" size="sm" className="w-fit" onClick={() => onChange(null)}>
              Remove the condition
            </Button>
          )}
        </>
      )}
    </div>
  );
}

function PredicateNodeFields({
  value,
  onChange,
  context,
  depth,
  onRemove,
  rootLabel = 'This rule fires when'
}: {
  value: unknown;
  onChange: (next: unknown) => void;
  context: IPayloadContext;
  depth: number;
  onRemove?: () => void;
  rootLabel?: string;
}) {
  const node = readNode(value);
  const kinds = usableKinds(context, node?.kind ?? null);

  // A node the grammar cannot read at all — an object with two keys, or a bare value where a
  // node belongs. Saying so beats rendering an empty form over it.
  if (!node) {
    return (
      <div className="grid gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2">
        <p className="text-sm">
          This is not a condition the grammar recognises — a node is one object with exactly one
          key, its kind.
        </p>
        <RawJsonField
          label="This node"
          value={value ?? null}
          onChange={onChange}
          disabled={context.disabled}
          rows={3}
        />
      </div>
    );
  }

  const known = KIND_BY_CODE.get(node.kind);

  return (
    <div
      className={cn(
        'grid gap-3',
        depth > 0 && 'border-l-2 border-[var(--color-slight-blue)] pl-3'
      )}
    >
      <div className="flex flex-wrap items-end gap-2">
        <ChoiceField
          label={depth === 0 ? rootLabel : 'And this one, when'}
          value={node.kind}
          options={kinds.map((entry) => ({ value: entry.kind, label: entry.label }))}
          allowUnset={false}
          onChange={(next) => {
            if (next === null || next === node.kind) return;
            const blank = KIND_BY_CODE.get(next)?.blank ?? (() => ({}));
            onChange(makeNode(next, blank()));
          }}
          disabled={context.disabled}
          className="min-w-64 flex-1"
        />
        <Badge variant="outline" className="mb-2 font-mono text-[10px]">
          {node.kind}
        </Badge>
        {onRemove && !context.disabled && (
          <Button variant="ghost" size="sm" className="mb-1" onClick={onRemove}>
            Remove
          </Button>
        )}
      </div>

      {known ? (
        <KindFields
          kind={node.kind}
          payload={node.payload}
          onChange={(next) => onChange(makeNode(node.kind, next))}
          context={context}
          depth={depth}
        />
      ) : (
        <RawJsonField
          label={`${node.kind} payload`}
          hint="This kind is newer than this screen. Its payload is kept as it is."
          value={node.payload ?? null}
          onChange={(next) => onChange(makeNode(node.kind, next))}
          disabled={context.disabled}
          rows={3}
        />
      )}
    </div>
  );
}

function KindFields({
  kind,
  payload,
  onChange,
  context,
  depth
}: {
  kind: string;
  payload: unknown;
  onChange: (next: unknown) => void;
  context: IPayloadContext;
  depth: number;
}) {
  const object = asObject(payload);
  const set = (key: string, next: unknown) => onChange(withMember(object, key, next));
  const ops = context.vocabulary?.comparisonOps ?? ['==', '!=', '>', '>=', '<', '<='];

  switch (kind) {
    // --- grouping ---------------------------------------------------------
    case 'all':
    case 'any': {
      const children = asArray(payload);
      return (
        <div className="grid gap-2">
          {children.length === 0 && (
            <p className="text-xs text-muted-foreground">
              Nothing here yet — an empty group is {kind === 'all' ? 'always true' : 'never true'}.
            </p>
          )}
          {children.map((child, index) => (
            <div key={index} className="rounded-md border border-border p-2">
              <PredicateNodeFields
                value={child}
                onChange={(next) => onChange(children.map((e, at) => (at === index ? next : e)))}
                context={context}
                depth={depth + 1}
                onRemove={() => onChange(children.filter((_, at) => at !== index))}
              />
            </div>
          ))}
          {!context.disabled && (
            <Button
              variant="outline"
              size="sm"
              className="w-fit"
              onClick={() => onChange([...children, makeNode('answer_eq', { ref: { usage: '' }, option: '' })])}
            >
              <Plus className="size-4" aria-hidden /> Add a condition
            </Button>
          )}
        </div>
      );
    }

    case 'not':
      return (
        <div className="rounded-md border border-border p-2">
          <PredicateNodeFields
            value={payload}
            onChange={onChange}
            context={context}
            depth={depth + 1}
          />
        </div>
      );

    // --- leaves -----------------------------------------------------------
    case 'const':
      return (
        <ChoiceField
          label="Value"
          value={payload === true ? 'true' : payload === false ? 'false' : ''}
          options={[
            { value: 'true', label: 'Always true' },
            { value: 'false', label: 'Always false' }
          ]}
          allowUnset={false}
          onChange={(next) => onChange(next === 'true')}
          disabled={context.disabled}
        />
      );

    case 'path_is':
      return (
        <TextField
          label="Path code"
          hint="The survey path the respondent has to be on."
          value={payload}
          onChange={(next) => onChange(next ?? '')}
          disabled={context.disabled}
          mono
        />
      );

    case 'answer_eq':
      return (
        <div className="grid gap-3">
          <AnswerRefField
            value={object?.ref}
            onChange={(next) => set('ref', next)}
            context={context}
          />
          <TextField
            label="Chosen option"
            hint="The option code, as the option list spells it."
            value={object?.option}
            onChange={(next) => set('option', next ?? '')}
            disabled={context.disabled}
            mono
          />
        </div>
      );

    case 'answer_in':
      return (
        <div className="grid gap-3">
          <AnswerRefField
            value={object?.ref}
            onChange={(next) => set('ref', next)}
            context={context}
          />
          <StringListField
            label="Any of these options"
            values={asArray(object?.options).map((option) => asString(option))}
            onChange={(next) => set('options', next)}
            disabled={context.disabled}
            addLabel="Add option"
          />
        </div>
      );

    case 'answer_cmp':
    case 'selected_count':
      return (
        <div className="grid gap-3">
          <AnswerRefField
            value={object?.ref}
            onChange={(next) => set('ref', next)}
            context={context}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <ChoiceField
              label="Is"
              value={object?.op}
              options={optionsOf(ops)}
              allowUnset={false}
              onChange={(next) => set('op', next)}
              disabled={context.disabled}
            />
            <NumberField
              label={kind === 'selected_count' ? 'This many options' : 'This value'}
              value={object?.value}
              onChange={(next) => set('value', next ?? 0)}
              disabled={context.disabled}
              integer={kind === 'selected_count'}
            />
          </div>
        </div>
      );

    case 'sum': {
      const against = asObject(object?.against);
      const comparesToRef = against !== null && 'ref' in against;
      return (
        <div className="grid gap-3">
          <AnswerRefField
            value={object?.of}
            onChange={(next) => set('of', next)}
            context={context}
            label="Sum of"
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <ChoiceField
              label="Is"
              value={object?.op}
              options={optionsOf(ops)}
              allowUnset={false}
              onChange={(next) => set('op', next)}
              disabled={context.disabled}
            />
            <ChoiceField
              label="Compared with"
              value={comparesToRef ? 'ref' : 'value'}
              options={[
                { value: 'value', label: 'A fixed number' },
                { value: 'ref', label: "Another answer's value" }
              ]}
              allowUnset={false}
              // Exactly one of value/ref, which the server checks — so switching replaces
              // rather than adds, and a shape carrying both never leaves this form.
              onChange={(next) =>
                set('against', next === 'ref' ? { ref: { usage: '' } } : { value: 0 })
              }
              disabled={context.disabled}
            />
          </div>

          {comparesToRef ? (
            <AnswerRefField
              value={against?.ref}
              onChange={(next) => set('against', { ref: next })}
              context={context}
              label="That answer"
            />
          ) : (
            <NumberField
              label="That number"
              value={against?.value}
              onChange={(next) => set('against', { value: next ?? 0 })}
              disabled={context.disabled}
            />
          )}
        </div>
      );
    }

    case 'repeat_count':
      return (
        <div className="grid gap-3 sm:grid-cols-3">
          <ChoiceField
            label="Repeat group"
            value={object?.usage}
            options={optionsOf(context.usageCodes)}
            allowUnset={false}
            onChange={(next) => set('usage', next)}
            disabled={context.disabled}
          />
          <ChoiceField
            label="Row count is"
            value={object?.op}
            options={optionsOf(ops)}
            allowUnset={false}
            onChange={(next) => set('op', next)}
            disabled={context.disabled}
          />
          <NumberField
            label="This many"
            value={object?.value}
            onChange={(next) => set('value', next ?? 0)}
            disabled={context.disabled}
            integer
          />
        </div>
      );

    case 'fact_exists':
      return (
        <div className="grid gap-3 sm:grid-cols-3">
          <ChoiceField
            label="Fact"
            value={object?.fact}
            options={optionsOf(context.vocabulary?.factKeys ?? [])}
            allowUnset={false}
            onChange={(next) => set('fact', next)}
            disabled={context.disabled}
          />
          <TextField
            label="Path inside it"
            value={object?.path}
            onChange={(next) => set('path', next)}
            disabled={context.disabled}
            mono
          />
          <TextField
            label="Status"
            value={object?.status}
            onChange={(next) => set('status', next)}
            disabled={context.disabled}
            mono
          />
        </div>
      );

    case 'fact_cmp':
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          <ChoiceField
            label="Fact"
            value={object?.fact}
            options={optionsOf(context.vocabulary?.factKeys ?? [])}
            allowUnset={false}
            onChange={(next) => set('fact', next)}
            disabled={context.disabled}
          />
          <TextField
            label="Path inside it"
            value={object?.path}
            onChange={(next) => set('path', next)}
            disabled={context.disabled}
            mono
          />
          <ChoiceField
            label="Is"
            value={object?.op}
            options={optionsOf(ops)}
            allowUnset={false}
            onChange={(next) => set('op', next)}
            disabled={context.disabled}
          />
          <NumberField
            label="This value"
            value={object?.value}
            onChange={(next) => set('value', next ?? 0)}
            disabled={context.disabled}
          />
        </div>
      );

    case 'db_state':
      return (
        <ChoiceField
          label="Check"
          hint="A named state the server can answer about this response."
          value={object?.check}
          options={optionsOf(context.vocabulary?.dbStateChecks ?? [])}
          allowUnset={false}
          onChange={(next) => set('check', next)}
          disabled={context.disabled}
        />
      );

    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Effects
// ---------------------------------------------------------------------------

/** What each effect type's `params` carries. Only four are typed on the server. */
const TYPED_PAYLOADS = ['set_state', 'set_variant', 'validate', 'set_text'];

/**
 * A rule's effects — what it does when it fires.
 *
 * The write replaces the whole list, which is why this is one editor over an array rather
 * than a row of independent forms: a partial send would delete the effects it omitted.
 */
export function EffectsEditor({
  value,
  onChange,
  context
}: {
  value: unknown[];
  onChange: (next: unknown[]) => void;
  context: IPayloadContext;
}) {
  const [json, setJson] = React.useState(false);
  const vocabulary = context.vocabulary;

  return (
    <div className="grid gap-3">
      <PayloadHeader
        label="Effects"
        description="What the rule does when it fires. Saved as a whole list, so removing one here removes it on the server."
        json={json}
        onJson={setJson}
      />

      {json ? (
        <RawJsonField
          label="effects"
          value={value}
          onChange={(next) => onChange(Array.isArray(next) ? next : [])}
          disabled={context.disabled}
          rows={8}
        />
      ) : (
        <div className="grid gap-2">
          {value.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No effects. A rule with none is accepted but does nothing.
            </p>
          )}

          {value.map((entry, index) => {
            const effect = asObject(entry) ?? {};
            const effectType = asString(effect.effectType);
            const set = (key: string, next: unknown) =>
              onChange(value.map((e, at) => (at === index ? withMember(effect, key, next) : e)));

            return (
              <RepeaterItem
                key={index}
                title={`Effect ${index + 1}${effectType ? ` · ${effectType}` : ''}`}
                disabled={context.disabled}
                onRemove={() => onChange(value.filter((_, at) => at !== index))}
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  <ChoiceField
                    label="Does"
                    value={effectType}
                    options={optionsOf(vocabulary?.effectTypes ?? [])}
                    allowUnset={false}
                    onChange={(next) => {
                      // A payload written for one effect type means nothing under another,
                      // so the type carries its own away with it.
                      const moved = withMember(effect, 'effectType', next);
                      onChange(
                        value.map((e, at) => (at === index ? withMember(moved, 'params', null) : e))
                      );
                    }}
                    disabled={context.disabled}
                  />
                  <ChoiceField
                    label="To"
                    hint="Which kind of thing it acts on."
                    value={effect.targetKind}
                    options={optionsOf(vocabulary?.enums.effectTargetKind ?? [])}
                    allowUnset={false}
                    onChange={(next) => set('targetKind', next)}
                    disabled={context.disabled}
                  />
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <ChoiceField
                    label="Question"
                    value={effect.targetUsage}
                    options={optionsOf(context.usageCodes)}
                    onChange={(next) => set('targetUsage', next)}
                    disabled={context.disabled}
                  />
                  <TextField
                    label="Section"
                    value={effect.targetSection}
                    onChange={(next) => set('targetSection', next)}
                    disabled={context.disabled}
                    mono
                  />
                  <TextField
                    label="Option code"
                    value={effect.targetOptionCode}
                    onChange={(next) => set('targetOptionCode', next)}
                    disabled={context.disabled}
                    mono
                  />
                </div>

                <ChoiceField
                  label="Severity"
                  value={effect.severity}
                  options={optionsOf(vocabulary?.enums.effectSeverity ?? [])}
                  onChange={(next) => set('severity', next)}
                  disabled={context.disabled}
                />

                <EffectParamsFields
                  effectType={effectType}
                  value={effect.params}
                  onChange={(next) => set('params', next)}
                  context={context}
                />
              </RepeaterItem>
            );
          })}

          {!context.disabled && (
            <Button
              variant="outline"
              size="sm"
              className="w-fit"
              onClick={() =>
                onChange([
                  ...value,
                  {
                    effectType: vocabulary?.effectTypes[0] ?? 'set_visibility',
                    targetKind: vocabulary?.enums.effectTargetKind[0] ?? 'usage',
                    targetUsage: null,
                    targetSection: null,
                    targetOptionCode: null,
                    severity: null,
                    params: null
                  }
                ])
              }
            >
              <Plus className="size-4" aria-hidden /> Add effect
            </Button>
          )}

          <p className="text-xs text-muted-foreground">
            Not every effect works against every target kind — the server refuses the pairs it
            cannot evaluate, and says which. It does not publish that map, so this form offers
            all four rather than guessing at a narrower list.
          </p>
        </div>
      )}
    </div>
  );
}

function EffectParamsFields({
  effectType,
  value,
  onChange,
  context
}: {
  effectType: string;
  value: unknown;
  onChange: (next: unknown) => void;
  context: IPayloadContext;
}) {
  const object = asObject(value);
  const set = (key: string, next: unknown) => onChange(emptyToNull(withMember(object, key, next)));

  if (!TYPED_PAYLOADS.includes(effectType)) {
    return (
      <RawJsonField
        label="Parameters"
        hint="The server does not type this effect's payload, so it stays JSON. Blank is usually right."
        value={value ?? null}
        onChange={onChange}
        disabled={context.disabled}
        rows={3}
      />
    );
  }

  if (effectType === 'set_variant') {
    return (
      <TextField
        label="Variant"
        hint="One of the variant keys on the target question's parameters."
        value={object?.variant}
        onChange={(next) => set('variant', next)}
        disabled={context.disabled}
        mono
      />
    );
  }

  if (effectType === 'set_text') {
    return (
      <FieldRow label="Replacement text" hint="Overrides the target question's label or helper.">
        <Textarea
          rows={2}
          value={asString(object?.text)}
          disabled={context.disabled}
          onChange={(event) => set('text', event.target.value)}
        />
      </FieldRow>
    );
  }

  if (effectType === 'validate') {
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        <ChoiceField
          label="Blocks at"
          value={object?.block}
          options={optionsOf(context.vocabulary?.enums.effectBlock ?? [])}
          allowUnset={false}
          onChange={(next) => set('block', next)}
          disabled={context.disabled}
        />
        <TextField
          label="Message"
          value={object?.message}
          onChange={(next) => set('message', next)}
          disabled={context.disabled}
        />
      </div>
    );
  }

  // set_state — the one effect payload that nests a predicate of its own.
  const cases = asArray(object?.cases);
  const fallback = asObject(object?.default);
  const visibilities = context.vocabulary?.enums.visibilityDefault ?? ['visible', 'hidden'];

  return (
    <div className="grid gap-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Cases</span>
        <Badge variant="outline">{cases.length}</Badge>
        <span className="text-xs text-muted-foreground">First match wins.</span>
        {!context.disabled && (
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={() =>
              set('cases', [...cases, { when: { const: true }, visibility: visibilities[0] }])
            }
          >
            <Plus className="size-4" aria-hidden /> Add case
          </Button>
        )}
      </div>

      {cases.map((entry, index) => {
        const branch = asObject(entry) ?? {};
        const write = (key: string, next: unknown) =>
          set('cases', cases.map((e, at) => (at === index ? withMember(branch, key, next) : e)));

        return (
          <RepeaterItem
            key={index}
            title={`Case ${index + 1}`}
            disabled={context.disabled}
            onRemove={() => set('cases', cases.filter((_, at) => at !== index))}
          >
            <PredicateEditor
              value={branch.when ?? null}
              onChange={(next) => write('when', next)}
              context={context}
              title="When"
              description="This case's own condition — a full predicate, nested inside the effect."
              rootLabel="This case applies when"
              allowEmpty={false}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <ChoiceField
                label="Then visibility"
                value={branch.visibility}
                options={optionsOf(visibilities)}
                allowUnset={false}
                onChange={(next) => write('visibility', next)}
                disabled={context.disabled}
              />
              <TriStateField
                label="And required"
                value={branch.required}
                onChange={(next) => write('required', next)}
                disabled={context.disabled}
                allowNull={false}
              />
            </div>
          </RepeaterItem>
        );
      })}

      <div className="rounded-md border border-dashed p-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Otherwise</span>
          {fallback === null ? (
            !context.disabled && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                onClick={() => set('default', { visibility: visibilities[0] })}
              >
                <Plus className="size-4" aria-hidden /> Add a fallback
              </Button>
            )
          ) : (
            !context.disabled && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                onClick={() => set('default', null)}
              >
                Remove
              </Button>
            )
          )}
        </div>
        {fallback === null ? (
          <p className="mt-1 text-xs text-muted-foreground">
            No fallback — when no case matches, the question keeps its own default.
          </p>
        ) : (
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <ChoiceField
              label="Visibility"
              value={fallback.visibility}
              options={optionsOf(visibilities)}
              allowUnset={false}
              onChange={(next) => set('default', withMember(fallback, 'visibility', next))}
              disabled={context.disabled}
            />
            <TriStateField
              label="Required"
              value={fallback.required}
              onChange={(next) => set('default', withMember(fallback, 'required', next))}
              disabled={context.disabled}
              allowNull={false}
            />
          </div>
        )}
      </div>
    </div>
  );
}
